import { ReactNode, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Grid, Typography } from '@mui/material';

import { getPercentFunded, shouldHideInvestorCount } from '@arrived/common';
import { useFeature, BlockedOfferingsFlag, FEATURE_FLAGS } from '@arrived/feature-flags';
import { Offering } from '@arrived/models';
import { useCanUserTransactEarlyAccess } from '@arrived/queries';

import { Numeral } from '../../Numeral';
import { PropertyMonetaryAvailabilityInfo } from '../PropertyMonetaryAvailabilityInfo';
import { PropertyProgressBar } from '../PropertyProgressBar';

import { useIsMobile } from '../../hooks';
import { NavigationBarContext } from '../../navigation';

interface PropertyDetailsCTAProps {
  offering: Offering;
  propertyActionButton: ReactNode;
  topOffset?: number;
}

export const PropertyDetailsCTA = ({ offering, propertyActionButton, topOffset }: PropertyDetailsCTAProps) => {
  const canUserTransactEarlyAccess = useCanUserTransactEarlyAccess();
  const isMobile = useIsMobile();
  const blockedOfferingsFlag = useFeature<BlockedOfferingsFlag>(FEATURE_FLAGS.BLOCKED_OFFERINGS);
  const isOfferingBlocked = useMemo(
    () => blockedOfferingsFlag.value?.properties.includes(offering.shortName),
    [offering.shortName, blockedOfferingsFlag]
  );
  const { offset, transition } = useContext(NavigationBarContext);

  if (isMobile) {
    return (
      <Grid item xs={12} className="property-details-sticky-footer">
        {!isOfferingBlocked &&
          !offering.isFund &&
          !shouldHideInvestorCount(offering.status, canUserTransactEarlyAccess) && (
            <PropertyProgressBar isLabelInBar offering={offering} />
          )}
        <div className="property-details-action-container">
          {!isOfferingBlocked && !offering.isFund && (
            <div className="property-details-header-item property-details-header-availability">
              <PropertyMonetaryAvailabilityInfo isAvailableInvested isStacked offering={offering} />
            </div>
          )}
          <Box className="property-details-header-item property-details-header-action">{propertyActionButton}</Box>
        </div>
      </Grid>
    );
  } else {
    return (
      <Grid item xs={12} md={4} lg={4}>
        <Box
          position="sticky"
          top={({ spacing }) => `calc(${(topOffset ?? 0) + offset}px + ${spacing(4)})`}
          borderRadius={0.5}
          border={({ palette }) => `1px solid ${palette.neutrals.platinum}`}
          style={{ transition: `top ${transition}` }}
        >
          <Grid container flexDirection="column" spacing={2} p={3}>
            {!isOfferingBlocked && !offering.isFund && (
              <Grid item>
                <PropertyMonetaryAvailabilityInfo
                  isAvailableInvested
                  monetaryTypography={{ variant: 'h4.bold' }}
                  offering={offering}
                  isStacked={false}
                  dataE2e="property-details-available-text"
                />
              </Grid>
            )}
            <Grid
              item
              sx={{
                '.MuiButton-root': {
                  maxWidth: 'unset',
                },
              }}
            >
              {propertyActionButton}
            </Grid>
          </Grid>
          {!isOfferingBlocked &&
            !offering.isFund &&
            !shouldHideInvestorCount(offering.status, canUserTransactEarlyAccess) && (
              <>
                <Grid container px={3} py={2} borderTop={({ palette }) => `1px solid ${palette.neutrals.platinum}`}>
                  <Grid item md={6}>
                    <Typography variant="h6.semibold" textTransform="uppercase">
                      <FormattedMessage
                        id="property-details-cta-percent-funded"
                        defaultMessage="{investors} investors"
                        values={{
                          investors: offering.investorsCount,
                        }}
                      />
                    </Typography>
                  </Grid>
                  <Grid item md={6} textAlign="right">
                    <Typography variant="h6.semibold" textTransform="uppercase">
                      <FormattedMessage
                        id="property-details-cta-percent-funded"
                        defaultMessage="{percent} funded"
                        values={{
                          percent: (
                            <Numeral
                              value={getPercentFunded(offering)}
                              type="percent"
                              minimumFractionDigits={0}
                              maximumFractionDigits={0}
                            />
                          ),
                        }}
                      />
                    </Typography>
                  </Grid>
                </Grid>
                <PropertyProgressBar isLabelVisible={false} offering={offering} />
              </>
            )}
        </Box>
      </Grid>
    );
  }
};
